import type { Segment } from '@nono/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HelpCircle, Menu, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { MethodologySheet } from '@/components/methodology/MethodologySheet';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { useT } from '@/lib/i18n';
import { useFreshness } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { DateRangePicker } from './DateRangePicker';
import { LocaleToggle } from './LocaleToggle';
import { ThemeToggle } from './ThemeToggle';

const timeFmt = new Intl.DateTimeFormat('nb-NO', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Oslo',
});

export function FilterBar({ onMenu }: { onMenu: () => void }) {
  const t = useT();
  const { segment, setSegment } = useFilters();
  const qc = useQueryClient();
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const freshness = useFreshness();
  const stamp = freshness.data?.refreshedAt
    ? timeFmt.format(new Date(freshness.data.refreshedAt))
    : null;

  // Re-sync: recompute today's metrics server-side, then refetch everything.
  // Spinner + disabled state give immediate feedback (the old button looked dead).
  const sync = useMutation({
    mutationFn: () => api.refresh(),
    onSettled: () => qc.invalidateQueries(),
  });

  return (
    <header className="sticky top-0 z-20 flex min-h-13 flex-wrap items-center gap-2 border-b border-border bg-bg/85 px-3 py-2 backdrop-blur sm:h-13 sm:flex-nowrap sm:gap-3 sm:px-6 sm:py-0">
      {/* hamburger — opens the drawer on mobile only */}
      <button
        type="button"
        onClick={onMenu}
        aria-label="Åpne meny"
        className="flex size-9 shrink-0 items-center justify-center rounded-md text-fg-muted transition-fast hover:bg-surface-2 hover:text-fg md:hidden"
      >
        <Menu className="size-5" aria-hidden />
      </button>

      <DateRangePicker />
      <SegmentToggle value={segment} onChange={setSegment} />

      {/* action cluster — pushed right; wraps to its own line on a narrow phone */}
      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        {stamp ? (
          <span className="hidden text-micro text-fg-subtle tabular lg:inline">
            {t.synced()} {stamp}
          </span>
        ) : null}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          title={sync.isPending ? t.syncing() : t.resync()}
          className="gap-1.5 text-fg-muted"
        >
          <RefreshCw className={cn('size-3.5', sync.isPending && 'animate-spin')} />
          <span className="hidden sm:inline">{sync.isPending ? t.syncing() : t.resync()}</span>
        </Button>

        <button
          type="button"
          title={t.methodology()}
          aria-label={t.methodology()}
          onClick={() => setMethodologyOpen(true)}
          className="flex size-9 items-center justify-center rounded-md text-fg-subtle transition-fast hover:bg-surface-2 hover:text-fg sm:size-8"
        >
          <HelpCircle className="size-4" aria-hidden />
        </button>

        <div className="mx-1 hidden h-5 w-px bg-border sm:block" />

        <LocaleToggle />
        <ThemeToggle />
      </div>

      <MethodologySheet open={methodologyOpen} onClose={() => setMethodologyOpen(false)} />
    </header>
  );
}

function SegmentToggle({ value, onChange }: { value: Segment; onChange: (s: Segment) => void }) {
  const t = useT();
  const items: { value: Segment; label: string; dot?: string }[] = [
    { value: 'all', label: t.segAll() },
    { value: 'lead', label: t.segLead(), dot: 'bg-accent-lead' },
    { value: 'booking', label: t.segBooking(), dot: 'bg-accent-booking' },
  ];

  return (
    <div className="flex h-9 items-center rounded-md border border-border bg-surface p-0.5 sm:h-8">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => onChange(item.value)}
          className={cn(
            'flex h-8 items-center gap-1.5 rounded px-2.5 text-label transition-fast sm:h-7',
            item.value === value
              ? 'bg-surface-3 font-medium text-fg'
              : 'text-fg-subtle hover:text-fg',
          )}
        >
          {item.dot ? <span className={cn('size-1.5 rounded-full', item.dot)} /> : null}
          {item.label}
        </button>
      ))}
    </div>
  );
}
